import { Component, inject, signal, OnInit, PLATFORM_ID } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import {
  AdminNotificationService,
  AdminNotification,
} from '../../services/admin-notification';
import { ToastService } from '../../services/toast';
import { PageTitleService } from '../../services/page-title';
import { IndDatePipe } from '../../pipes/ind-date';

type ReadFilter = 'all' | 'unread';

@Component({
  selector: 'app-admin-notifications',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    TagModule,
    SelectModule,
    TooltipModule,
    IndDatePipe,
  ],
  templateUrl: './admin-notifications.html',
  styleUrls: ['./admin-notifications.css'],
})
export class AdminNotificationsPage implements OnInit {
  private svc = inject(AdminNotificationService);
  private toast = inject(ToastService);
  private router = inject(Router);
  readonly pageTitle = inject(PageTitleService).title;
  private platformId = inject(PLATFORM_ID);

  readonly filterOptions: { label: string; value: ReadFilter }[] = [
    { label: 'All notifications', value: 'all' },
    { label: 'Unread only', value: 'unread' },
  ];

  loading = signal(false);
  filter = signal<ReadFilter>('all');
  rows = signal<AdminNotification[]>([]);

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) this.load();
  }

  load() {
    this.loading.set(true);
    this.svc.getAll(this.filter() === 'unread', 200).subscribe({
      next: (res: any) => {
        this.loading.set(false);
        const data = res?.data ?? res ?? [];
        this.rows.set(Array.isArray(data) ? data : []);
      },
      error: () => this.loading.set(false),
    });
  }

  onFilter(v: ReadFilter) {
    this.filter.set(v ?? 'all');
    this.load();
  }

  /** Open a notification: mark it read, then jump to the related KYC review (refId = partnerId). */
  open(n: AdminNotification) {
    const go = () => {
      if (n.refId != null) {
        this.router.navigate(['/partner-kyc-review'], { queryParams: { partnerId: n.refId } });
      }
    };
    if (n.isRead) {
      go();
      return;
    }
    this.svc.markRead(n.id).subscribe({
      next: () => {
        this.applyRead(n.id);
        go();
      },
      error: go,
    });
  }

  markRead(n: AdminNotification) {
    if (n.isRead) return;
    this.svc.markRead(n.id).subscribe({
      next: () => this.applyRead(n.id),
    });
  }

  markAllRead() {
    this.svc.markAllRead().subscribe({
      next: () => {
        this.svc.unread.set(0);
        this.rows.set(this.rows().map((x) => ({ ...x, isRead: true })));
        // If we're viewing unread-only, the list is now empty after a reload.
        if (this.filter() === 'unread') this.load();
        this.toast.success('All notifications marked read');
      },
    });
  }

  /** Reflect a single read locally and decrement the shared badge. */
  private applyRead(id: number) {
    this.svc.unread.set(Math.max(0, this.svc.unread() - 1));
    if (this.filter() === 'unread') {
      this.rows.set(this.rows().filter((x) => x.id !== id));
    } else {
      this.rows.set(this.rows().map((x) => (x.id === id ? { ...x, isRead: true } : x)));
    }
  }

  get unreadCount() {
    return this.rows().filter((x) => !x.isRead).length;
  }
}
